// biome-ignore-all lint/style/useNamingConvention: all external data
import { z } from 'zod';

export const Widget = z.object({
  data: z.string().optional(),
  databaseId: z.number().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
});

export const Taxonomy = z.object({
  order: z.number(),
  position: z.union([z.literal('header'), z.literal('in_stream'), z.literal('right_side'), z.literal('footer')]),
  term: z.string(),
});

export type Taxonomy = z.infer<typeof Taxonomy>;

export const WidgetComponent = z.object({
  components: z.array(Widget),
  slug: z.string(),
  taxonomies: z.array(Taxonomy).nullable(),
  templateSlug: z.string(),
});

export type WidgetComponent = z.infer<typeof WidgetComponent>;

export const WidgetResponse = z.object({
  widgets: z.object({
    data: z.array(WidgetComponent).default([]),
  }),
});

export const EducationWidgetFeatures = z
  .object({
    columns: z.number(),
    data: z.record(z.string(), z.array(z.string())),
    has_footer: z.number(),
    has_header: z.number(),
    rows: z.number(),
  })
  .optional();

export type EducationWidgetFeatures = z.infer<typeof EducationWidgetFeatures>;

export const WidgetData = z.array(WidgetComponent);

export type WidgetResponse = z.infer<typeof WidgetResponse>;

export type WidgetData = z.infer<typeof WidgetData>;

export type Widget = z.infer<typeof Widget>;

export type WidgetPlacement = Map<Taxonomy['position'] | null, WidgetComponent[]>;

export type WidgetBuilder = ReturnType<typeof WidgetBuilder>;

export function WidgetBuilder(widgetData: WidgetData) {
  return {
    build: <T>(index = 0) => {
      const components = widgetData[index]?.components || [];
      const componentFields = components || [];
      const map = componentFields.reduce((acc, field) => {
        if (field.name) {
          return acc.set(field.name as T, field.data);
        }
        return acc;
      }, new Map<T, string | undefined>());

      return {
        get: (name: T) => map.get(name),
      };
    },
    buildMeta: <T>(index = 0) => {
      const response = widgetData || [];
      const data = {
        slug: response[index]?.slug || '',
        templateSlug: response[index]?.templateSlug || '',
      };
      return {
        get: (name: T) => data[name as keyof typeof data],
      };
    },
  };
}

export function generateWidgetPlacement(widgetResponse: WidgetResponse | null): WidgetPlacement {
  const map: WidgetPlacement = new Map<Taxonomy['position'] | null, WidgetComponent[]>();
  if (!widgetResponse) {
    return map;
  }
  const widgets = widgetResponse.widgets.data || [];

  for (const widget of widgets) {
    if (!widget.taxonomies) {
      continue;
    }
    const taxonomy = widget.taxonomies[0];
    const position = taxonomy?.position ?? null;

    if (position !== undefined) {
      map.set(position, (map.get(position) || []).concat(widget));
    }
  }
  return map;
}

export function parseNestedFields<T>(data: string | undefined) {
  if (!data) {
    return null;
  }
  try {
    const parsed = JSON.parse(data) as T;
    return parsed;
  } catch (error) {
    // TODO: Log this error
    return null;
  }
}
